import { FileTextOutlined, SyncOutlined, TeamOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import AdminPage from "admin/admin_page";
import { getUsersOrganizations } from "admin/api/organization";
import { getShowTrainingDataLink, JobState } from "admin/job/job_list_view";
import { getAiModels, updateAiModel } from "admin/rest_api";
import {
  App,
  Button,
  Col,
  Flex,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  type TableProps,
  Typography,
} from "antd";
import FormattedDate from "components/formatted_date";
import FormattedId from "components/formatted_id";
import LinkButton from "components/link_button";
import Markdown from "libs/markdown_adapter";
import { useFetch } from "libs/react_helpers";
import { useWkSelector } from "libs/react_hooks";
import Toast from "libs/toast";
import { filterWithSearchQueryAND, scrollToTop } from "libs/utils";
import uniq from "lodash-es/uniq";
import type { Key } from "react";
import { useState } from "react";
import { Link } from "react-router";
import { ModalWidth } from "theme";
import type { AiModel } from "types/api_types";
import { enforceActiveUser, formatUserName } from "viewer/model/accessors/user_accessor";

const { Search } = Input;

export default function AiModelListView() {
  const activeUser = useWkSelector((state) => enforceActiveUser(state.activeUser));
  const [currentlyEditedModel, setCurrentlyEditedModel] = useState<AiModel | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { modal } = App.useApp();

  const {
    data: aiModels = [],
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["aiModels"],
    queryFn: async () => {
      try {
        return await getAiModels();
      } catch (err) {
        Toast.error("Could not load model list.");
        console.error(err);
        throw err;
      }
    },
  });

  const columns: TableProps<AiModel>["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, model: AiModel) => (
        <Space orientation="vertical">
          {name}
          <FormattedId id={model.id} />
        </Space>
      ),
    },
    {
      title: "Created at",
      key: "created",
      defaultSortOrder: "descend",
      sorter: (a: AiModel, b: AiModel) => a.created - b.created,
      render: (model: AiModel) => <FormattedDate timestamp={model.created} />,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user: AiModel["user"]) => formatUserName(activeUser, user),
      filters: uniq(aiModels.map((model) => formatUserName(null, model.user))).map((username) => ({
        text: username,
        value: username,
      })),
      onFilter: (value: Key | boolean, model: AiModel) =>
        formatUserName(null, model.user).startsWith(String(value)),
      filterSearch: true,
    },
    {
      title: "Status",
      dataIndex: "trainingJob",
      key: "status",
      render: (trainingJob: AiModel["trainingJob"]) =>
        trainingJob && <JobState job={trainingJob} />,
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      render: (comment: AiModel["comment"]) => <Markdown>{comment}</Markdown>,
    },
    {
      title: "Actions",
      render: (aiModel: AiModel) =>
        renderActionsForModel(modal, aiModel, () => setCurrentlyEditedModel(aiModel)),
      key: "actions",
    },
  ];

  return (
    <>
      {currentlyEditedModel ? (
        <EditModelSharedOrganizationsModal
          model={currentlyEditedModel}
          onClose={() => {
            setCurrentlyEditedModel(null);
            refetch();
          }}
          owningOrganization={activeUser.organization}
        />
      ) : null}
      <AdminPage
        title="AI Models"
        descriptionURI="https://docs.webknossos.org/webknossos/automation/index.html"
        description="Review available AI models, compare training status and ownership, and manage cross-organization access for models owned by your organization."
        actions={
          <Button onClick={() => refetch()} icon={<SyncOutlined spin={isFetching} />}>
            Refresh
          </Button>
        }
        search={
          <Search
            allowClear
            onChange={(event) => setSearchQuery(event.target.value)}
            value={searchQuery}
          />
        }
      >
        <Spin spinning={isFetching} size="large">
          <Table
            rowKey={(run: AiModel) => `${run.id}`}
            pagination={{ pageSize: 100, onChange: scrollToTop }}
            columns={columns}
            dataSource={filterWithSearchQueryAND(
              aiModels,
              [
                "name",
                "comment",
                (model) => formatUserName(null, model.user),
                (model) => model.trainingJob?.state || "",
              ],
              searchQuery,
            )}
          />
        </Spin>
      </AdminPage>
    </>
  );
}

const renderActionsForModel = (
  modal: ReturnType<typeof App.useApp>["modal"],
  model: AiModel,
  onChangeSharedOrganizations: () => void,
) => {
  const organizationSharingButton =
    model.isOwnedByUsersOrganization && !model.isPretrained ? (
      <LinkButton onClick={onChangeSharedOrganizations} icon={<TeamOutlined />}>
        Manage Access
      </LinkButton>
    ) : null;
  if (model.trainingJob == null) {
    return organizationSharingButton;
  }
  const { voxelyticsWorkflowHash, state: trainingJobState } = model.trainingJob;
  const trainingAnnotations = model.trainingJob.args.trainingAnnotations;

  return (
    <Col>
      {trainingJobState === "SUCCESS" ? <Row>{organizationSharingButton}</Row> : null}
      {voxelyticsWorkflowHash != null ? (
        <Row>
          <Link to={`/workflows/${voxelyticsWorkflowHash}`}>
            <LinkButton icon={<FileTextOutlined />}>Voxelytics Report</LinkButton>
          </Link>
        </Row>
      ) : null}
      {trainingAnnotations != null ? (
        <Row>{getShowTrainingDataLink(modal, trainingAnnotations)}</Row>
      ) : null}
    </Col>
  );
};

function EditModelSharedOrganizationsModal({
  model,
  onClose,
  owningOrganization,
}: {
  model: AiModel;
  onClose: () => void;
  owningOrganization: string;
}) {
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>(
    model.sharedOrganizationIds || [owningOrganization],
  );
  const usersOrganizations = useFetch(getUsersOrganizations, [], []);
  const options = usersOrganizations.map((org) => {
    const additionalProps =
      org.id === owningOrganization
        ? { disabled: true, title: "Cannot remove owning organization from model." }
        : {};
    return { label: org.name, value: org.id, ...additionalProps };
  });

  const handleChange = (organizationIds: string[]) => {
    if (!organizationIds.some((id) => id === owningOrganization)) {
      organizationIds.push(owningOrganization);
    }
    setSelectedOrganizationIds(organizationIds);
  };

  const submitNewSharedOrganizations = async () => {
    try {
      const updatedModel = { ...model, sharedOrganizationIds: selectedOrganizationIds };
      await updateAiModel(updatedModel);
      Toast.success(
        `Successfully updated organizations that can access model ${updatedModel.name}.`,
      );
      onClose();
    } catch (e) {
      Toast.error("Failed to update shared organizations. See console for details.");
      console.error("Failed to update shared organizations.", e);
    }
  };

  return (
    <Modal
      title={"Edit Organizations with Access to this AI Model"}
      open
      onOk={submitNewSharedOrganizations}
      onCancel={onClose}
      mask={{ closable: false }}
      width={ModalWidth.Large}
    >
      <p>
        Select all organizations that should have access to the AI model{" "}
        <Typography.Text italic>{model.name}</Typography.Text>.
      </p>
      <Typography.Paragraph type="secondary">
        You can only manage access for organizations you belong to. Other members of your
        organization may have access to additional organizations not listed here. Only they can
        modify access for those organizations.
      </Typography.Paragraph>
      <Flex justify="center">
        <Select
          mode="multiple"
          allowClear
          autoFocus
          style={{ minWidth: 400 }}
          popupMatchSelectWidth={false}
          placeholder="Please select"
          onChange={handleChange}
          options={options}
          showSearch={{
            filterOption: (input, option) =>
              option != null &&
              (option.label.toLowerCase().includes(input.toLowerCase()) ||
                option.value.toLowerCase().includes(input.toLowerCase())),
          }}
          value={selectedOrganizationIds}
        />
      </Flex>
    </Modal>
  );
}
